<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="summaryGrid"
            :column-defs="summaryColumnDefs"
            :row-data="summaryRowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="summaryPinnedBottomRowData"
            :overlayNoRowsTemplate="overlayNoRowsTemplate"
            :is-auto-width="true"
            :is-auto-height="true"
            :is-search="false"
            grid-theme="ag-theme-alpine-report"
            class="salesperson-summary-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-lg-flex d-none align-items-baseline mr-1 mb-1 filter-properties">
                <form-search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Filter Properties"
                />
            </div>
            <div class="d-lg-flex d-none align-items-baseline mr-1 mb-1 filter-originators">
                <form-search-multiselect
                    v-model="filterOriginators"
                    :options="filterOriginatorOptions"
                    :icon="['fas', 'user']"
                    :responsiveness="false"
                    variant="primary"
                    placeholder="Filter Originators"
                />
            </div>
            <div class="d-lg-block d-none">
                <responsive-dropdown :items="filterByOptions" :responsiveness="false" variant="primary">
                    {{ filterByLabel }}
                </responsive-dropdown>
            </div>
            <div class="d-lg-block d-none">
                <form-square-switch
                    v-model="showUnconfirmedInvoices"
                    :labels="{
                        checked: 'Show Unconfirmed Invoices',
                        unchecked: 'Show Unconfirmed Invoices',
                    }"
                    class="show-unconfirmed-invoices-toggle mr-1"
                    :sync="true"
                    @input="fetchData"
                />
            </div>
            <export-to-excel-button :href="exportExcelHref"></export-to-excel-button>
            <exportToPDFButton :href="exportPdfHref" :pdfCacheKey="pdfCacheKey" />

            <b-button-group>
                <more-filters-dropdown class="filters-dropdown d-block d-lg-none mr-1">
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <form-search-multiselect
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            :responsiveness="false"
                            variant="primary"
                            placeholder="Filter Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-search-multiselect
                            v-model="filterOriginators"
                            :options="filterOriginatorOptions"
                            :icon="['fas', 'user']"
                            :responsiveness="false"
                            variant="primary"
                            placeholder="Filter Originators"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <responsive-dropdown
                            :items="filterByOptions"
                            :responsiveness="false"
                            variant="primary"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        >
                            {{ filterByLabel }}
                        </responsive-dropdown>
                        <form-square-switch
                            v-model="showUnconfirmedInvoices"
                            :labels="{
                                checked: 'Show Unconfirmed Invoices',
                                unchecked: 'Show Unconfirmed Invoices',
                            }"
                            class="show-unconfirmed-invoices-toggle"
                            :sync="true"
                            @input="fetchData"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" />
        </grid>
        <grid
            ref="detailGrid"
            :column-defs="detailColumnDefs"
            :row-data="detailRowData"
            :get-row-style="getRowStyle"
            :pinned-bottom-row-data="detailPinnedBottomRowData"
            :overlayNoRowsTemplate="overlayNoRowsTemplate"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            grid-theme="ag-theme-alpine-report"
            class="h-100 salesperson-detail-report"
        >
            <div class="mt-3" />
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import API from '../../api';
    import Page from '../generic/page/page.vue';
    import Grid from '../generic/grid/grid.vue';
    import ExportToExcelButton from '../generic/export-to-excel-button/export-to-excel-button.vue';
    import ResponsiveDropdown from '../generic/responsive-dropdown/responsive-dropdown.vue';
    import MoreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import FormSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
    import FormSearchMultiselect from '../generic/form-search-multiselect/form-search-multiselect.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        THIS_WEEK,
        LAST_WEEK,
        THIS_YEAR,
        LAST_YEAR,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import {
        REPORT_SALESPERSON_DETAIL_ENDPOINT,
        REPORT_SALESPERSON_DETAIL_EXCEL_ENDPOINT,
        REPORT_SALESPERSON_DETAIL_ORIGINATORS_ENDPOINT,
        REPORT_SALESPERSON_DETAIL_PROPERTIES_ENDPOINT,
        REPORT_SALESPERSON_DETAIL_PDF_ENDPOINT,
    } from '../../endpoints';
    import ReportSummaryBar from './report-summary-bar.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import { isMobileScreen } from '../../utils/screens';
    import { truncate } from '../../utils/strings';
    import ReportSettingsModal, {
        DATE_PERIODS_INPUT,
        SELECT_INPUT,
        SWITCH_INPUT,
        MULTI_SELECT_INPUT,
    } from './report-settings/report-settings-modal.vue';
    import { defineAsyncComponent } from 'vue';
    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../generic/export-to-pdf-button/export-to-pdf-button.vue'
        )
    );

    export default {
        name: 'SalespersonDetailReport',
        components: {
            Page,
            Grid,
            DateRangePicker,
            ResponsiveDropdown,
            FormSquareSwitch,
            FormSearchMultiselect,
            MoreFiltersDropdown,
            reportSummaryBar: ReportSummaryBar,
            ExportToExcelButton,
            exportToPDFButton,
            ReportSettingsModal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Salesperson Detail Report',
                tabs: [
                    {
                        title: 'Salesperson Detail',
                        route: '/app/reports/salesperson-detail/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(THIS_MONTH),
                overlayNoRowsTemplate: '<span></span>',
                predefinedRanges: [
                    TODAY,
                    YESTERDAY,
                    THIS_WEEK,
                    LAST_WEEK,
                    THIS_MONTH,
                    LAST_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                detailGridApi: null,
                detailRowData: [],
                summaryRowData: [],
                totalNights: 0,
                totalRevenue: 0,
                totalTax: 0,
                totalRentalRevenue: 0,
                totalFeeRevenue: 0,
                totalServiceRevenue: 0,
                totalOtherDeposits: 0,
                totalPercentage: 0,
                totalInvoices: 0,
                totalInquiries: 0,
                getRowStyle: null,
                filterProperties: [],
                filterPropertyOptions: [],
                filterOriginators: [],
                filterOriginatorOptions: [],
                filterBy: 'ENTERED',
                filterByLabel: 'Use Entered Date',
                showUnconfirmedInvoices: false,
                statusBar: null,
                componentInstance: null,
                hasRoom: false,
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            summaryColumnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Team Member',
                        minWidth: 100,
                    },
                    {
                        field: 'inquiries',
                        headerName: 'Inquiries',
                        minWidth: 100,
                    },
                    {
                        field: 'invoices',
                        headerName: 'Invoices',
                        minWidth: 100,
                    },
                    {
                        field: 'percentage',
                        headerName: 'Percentage',
                        minWidth: 100,
                        type: 'amountColumn',
                        cellRendererParams: {
                            percentage: true,
                        },
                    },
                    {
                        field: '',
                        headerName: '',
                        autoWidthExclude: true,
                        pdfExportOptions: {
                            skipColumn: true,
                        },
                        sortable: false,
                    },
                ];
            },
            detailColumnDefs() {
                return [
                    {
                        headerName: '#',
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.detailRowData.length : params.node.rowIndex + 1;
                        },
                        minWidth: 40,
                    },
                    {
                        field: 'entered',
                        headerName: 'Entered Date',
                        type: 'dateColumn',
                        minWidth: 100,
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => {
                                return format(parseISO(value), 'MM/dd/yy');
                            },
                        },
                    },
                    {
                        field: 'creator_name',
                        headerName: 'Originator',
                        minWidth: 80,
                    },
                    {
                        field: 'invoice_pk',
                        headerName: 'Invoice #',
                        minWidth: 80,
                        cellRenderer: (params) => {
                            const { node, data, value } = params;
                            if (!node.rowPinned) {
                                return `<a target="_self" href="/app/reservation/edit/invoice/${value}">${value}</a>`;
                            }
                            return '';
                        },
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        minWidth: 80,
                    },
                    {
                        field: 'property',
                        headerName: 'Unit Name',
                        minWidth: 80,
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => {
                                return value.length > 30 ? truncate(value, 30) : value;
                            },
                        },
                    },
                    {
                        field: 'room',
                        headerName: 'Unit #',
                        minWidth: 80,
                        pdfExportOptions: {
                            skipColumn: !this.hasRoom,
                        },
                    },
                    {
                        field: 'from_date',
                        headerName: 'Arrive',
                        minWidth: 80,
                        type: 'dateColumn',
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => {
                                return format(parseISO(value), 'MM/dd/yy');
                            },
                        },
                    },
                    {
                        field: 'to_date',
                        headerName: 'Depart',
                        minWidth: 80,
                        type: 'dateColumn',
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => {
                                return format(parseISO(value), 'MM/dd/yy');
                            },
                        },
                    },
                    {
                        field: 'nights',
                        headerName: 'Nights',
                        minWidth: 80,
                    },
                    {
                        field: 'rental_revenue',
                        headerName: 'Rental Revenues',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'service_revenue',
                        headerName: 'Service Revenues',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'fee_revenue',
                        headerName: 'Fee Revenues',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'tax',
                        headerName: 'Taxes',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'revenue',
                        headerName: 'Total Revenues',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: 'other_deposits',
                        headerName: 'Other Deposits',
                        minWidth: 100,
                        type: 'amountColumn',
                    },
                    {
                        field: '',
                        headerName: '',
                        autoWidthExclude: true,
                        pdfExportOptions: {
                            skipColumn: true,
                        },
                        sortable: false,
                    },
                ];
            },
            exportExcelHref() {
                let url = REPORT_SALESPERSON_DETAIL_EXCEL_ENDPOINT();
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.filterProperties.join(',')}`;
                url += `&originators=${this.filterOriginators.join(',')}`;
                url += `&show_unconfirmed=${this.showUnconfirmedInvoices}`;
                url += `&query_parameter=${this.filterBy}`;
                return url;
            },
            exportPdfHref() {
                let url = REPORT_SALESPERSON_DETAIL_PDF_ENDPOINT();
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&properties=${this.filterProperties.join(',')}`;
                url += `&originators=${this.filterOriginators.join(',')}`;
                url += `&show_unconfirmed=${this.showUnconfirmedInvoices}`;
                url += `&query_parameter=${this.filterBy}`;
                return url;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            filterByOptions() {
                return [
                    {
                        label: 'Use Entered Date',
                        click: () => {
                            this.filterByLabel = 'Use Entered Date';
                            this.filterBy = 'ENTERED';
                            this.fetchData();
                            this.componentInstance.setCaption(
                                'Invoices contained in this report are determined by the date entered into Lodgix.'
                            );
                        },
                    },
                    {
                        label: 'Use Arrival Date',
                        click: () => {
                            this.filterByLabel = 'Use Arrival Date';
                            this.filterBy = 'ARRIVAL';
                            this.fetchData();
                            this.componentInstance.setCaption(
                                'Invoices contained in this report are determined by the reservation arrival date.'
                            );
                        },
                    },
                ];
            },
            detailPinnedBottomRowData() {
                return [
                    {
                        nights: this.totalNights || 0,
                        rental_revenue: this.totalRentalRevenue || 0,
                        service_revenue: this.totalServiceRevenue || 0,
                        fee_revenue: this.totalFeeRevenue || 0,
                        tax: this.totalTax || 0,
                        revenue: this.totalRevenue || 0,
                        other_deposits: this.totalOtherDeposits || 0,
                    },
                ];
            },
            summaryPinnedBottomRowData() {
                return [
                    {
                        percentage: this.totalPercentage || 0,
                        inquiries: this.totalInquiries || 0,
                        invoices: this.totalInvoices || 0,
                    },
                ];
            },
            isMobile() {
                return this.isMobileScreen();
            },
            reportSettings() {
                return {
                    name: 'Sales Person Detail',
                    report_sysname: 'SALES_PERSON_DETAIL',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.predefinedRanges,
                            label: 'Period',
                            name: 'from_date',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            label: 'Only for Properties',
                            name: 'properties',
                            options: this.filterPropertyOptions,
                            placeholder: 'All Properties',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            label: 'Originators',
                            name: 'originators',
                            options: this.filterOriginatorOptions,
                            placeholder: 'All Originators',
                        },
                        {
                            type: SELECT_INPUT,
                            value: 'ARRIVAL',
                            name: 'query_parameter',
                            label: 'Filter By',
                            options: [
                                {
                                    text: 'Use Arrival Date',
                                    value: 'ARRIVAL',
                                },
                                {
                                    text: 'Use Entered Date',
                                    value: 'ENTERED',
                                },
                            ],
                        },
                        {
                            type: SWITCH_INPUT,
                            value: false,
                            label: 'Show Unconfirmed Invoices',
                            name: 'show_unconfirmed',
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchProperties();
            this.fetchOriginators();
            this.fetchData();
            const detailGrid = this.$refs.detailGrid;
            this.detailGridApi = detailGrid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            isMobileScreen,
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_SALESPERSON_DETAIL_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            properties: this.filterProperties.join(','),
                            originators: this.filterOriginators.join(','),
                            show_unconfirmed: this.showUnconfirmedInvoices,
                            query_parameter: this.filterBy,
                        },
                    });
                    this.pdfCacheKey = data.pdf_cache_key;
                    this.detailRowData = data.sales.map((item, index) => {
                        return {
                            ...item,
                            id: index,
                        };
                    });
                    this.summaryRowData = data.summary.list.map((item, index) => {
                        return {
                            ...item,
                            id: index,
                        };
                    });
                    this.hasRoom = this.detailRowData.filter((item) => item.room && item.room.length > 0).length > 0;
                    this.totalNights = data.total_nights;
                    this.totalRevenue = data.total_revenue;
                    this.totalTax = data.total_tax;
                    this.totalServiceRevenue = data.total_service_revenue;
                    this.totalFeeRevenue = data.total_fee_revenue;
                    this.totalRentalRevenue = data.total_rental_revenue;
                    this.totalOtherDeposits = data.total_other_deposits;
                    this.totalPercentage = this.summaryRowData.length === 0 ? 0 : 100;
                    this.totalInvoices = data.summary.total_hits;
                    this.totalInquiries = data.summary.total_inquiries;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchProperties() {
                const { data } = await API.get(REPORT_SALESPERSON_DETAIL_PROPERTIES_ENDPOINT());
                this.filterPropertyOptions = data.map((item) => ({
                    value: item.property_id,
                    text: item.name,
                    disabled: item.disabled,
                }));
            },
            async fetchOriginators() {
                const { data } = await API.get(REPORT_SALESPERSON_DETAIL_ORIGINATORS_ENDPOINT());
                this.filterOriginatorOptions = data
                    .filter((item) => item.name)
                    .map((item) => ({
                        value: item.id,
                        text: `${item.name}`,
                        disabled: false,
                    }));
            },
            onResize() {
                const statusBarComponent = this.detailGridApi.getStatusPanel('reportSummaryBar');
                this.componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    this.componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                this.componentInstance.setVisible(this.isMobile);
                this.componentInstance.setTitle(this.title);
                this.componentInstance.setCaption(
                    this.filterBy === 'ENTERED'
                        ? 'Invoices contained in this report are determined by the date entered into Lodgix.'
                        : 'Invoices contained in this report are determined by the reservation arrival date.'
                );
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
        },
        watch: {
            filterProperties: {
                handler() {
                    this.fetchData();
                },
                deep: true,
            },
            filterOriginators: {
                handler() {
                    this.fetchData();
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss">
    .salesperson-summary-report {
        display: inline;
        .filter-properties,
        .filter-originators {
            > fieldset {
                margin: 0 !important;
            }
        }
        .show-unconfirmed-invoices-toggle {
            width: 210px;
            .v-switch-label {
                padding-top: 0;
                line-height: 34px !important;
            }
            .v-switch-core {
                height: 34px;
                .v-switch-button {
                    height: 28px;
                    top: 3px;
                }
            }
            &-mobile {
                width: 100%;
                .v-switch-label {
                    padding-top: 0;
                    line-height: 38px !important;
                }
                .v-switch-core {
                    height: 38px;
                    .v-switch-button {
                        height: 32px;
                        top: 3px;
                    }
                }
            }
        }
        .filters-dropdown {
            .show-unconfirmed-invoices-toggle {
                width: 250px;
            }
            fieldset {
                width: 100%;
            }
        }
    }
    .salesperson-detail-report {
        display: inline;
    }
</style>
